import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InputMask from 'react-input-mask';
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import './App.css';

function App() {
  // ---------- Auth ----------
  const [isAuthed, setIsAuthed] = useState(() => localStorage.getItem('auth') === 'true');
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // ---------- Estado principal ----------
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [units, setUnits] = useState([]);
  const [activeTab, setActiveTab] = useState('vendas');
  const [formData, setFormData] = useState({});
  const [records, setRecords] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  // DARK MODE POR PADRÃO
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true; // true por padrão
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const COLORS = ['#667eea', '#764ba2', '#f59e0b', '#10b981', '#ef4444'];
  const LOGO_URL = 'https://163b41deb46b4fc47ff5da637dee4f28.cdn.bubble.io/f1734114328378x328789701215075600/logo%20bless.zip%20-%201%20%282%29.png';

  // ---------- Utils: WhatsApp ----------
  const sanitizeDigits = (str = '') => (str.match(/\d+/g) || []).join('');
  const toWhatsAppLinkBR = (raw) => {
    let digits = sanitizeDigits(raw);
    if (!digits) return null;
    if (digits.startsWith('55')) digits = digits.slice(2);
    if (digits.length < 10) return null;
    const ddd = digits.slice(0, 2);
    let local = digits.slice(2);
    const dddNum = parseInt(ddd, 10);
    if (dddNum > 28 && local.length === 8) local = '9' + local;
    if (local.length > 9) local = local.slice(0, 9);
    return `https://wa.me/55${ddd}${local}`;
  };

  const motivoLabel = (m) => ({
    saude: 'Problemas de Saúde',
    trabalho: 'Trabalho',
    viagem: 'Viagem',
    financeiro: 'Questões Financeiras',
    pessoal: 'Questões Pessoais',
    mudanca: 'Mudança de Cidade',
    tempo: 'Falta de Tempo',
    outro: 'Outro'
  }[m] || m);

  // ---------- Tema inicial ----------
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Buscar unidades após login
  useEffect(() => {
    if (isAuthed) fetchUnits();
  }, [isAuthed]);

  // Reagir a abas/filtro/unidade
  useEffect(() => {
    if (!selectedUnit) return;
    if (activeTab === 'dashboard') {
      fetchDashboardData();
    } else if (activeTab === 'retornos') {
      fetchPendingReturns();
      setSearchTerm('');
    } else {
      fetchRecords();
    }
  }, [activeTab, dateFilter, selectedUnit]);

  // ---------- API ----------
  const fetchUnits = async () => {
    try {
      const { data } = await axios.get('/api/unidades');
      setUnits(data || []);
    } catch (e) {
      console.error('Erro ao buscar unidades:', e);
      setUnits([]);
    }
  };

  const fetchRecords = async () => {
    if (!selectedUnit) return;
    try {
      const { data } = await axios.get(`/api/${activeTab}/${selectedUnit.id}`);
      setRecords(data || []);
    } catch (e) {
      console.error('Erro ao buscar registros:', e);
      setRecords([]);
    }
  };

  const fetchPendingReturns = async () => {
    if (!selectedUnit) return;
    try {
      const { data } = await axios.get(`/api/ausentes/${selectedUnit.id}/pendentes`);
      setPendingReturns(data || []);
    } catch (e) {
      console.error('Erro ao buscar retornos pendentes:', e);
      setPendingReturns([]);
    }
  };

  const fetchDashboardData = async () => {
    if (!selectedUnit) return;
    try {
      const params =
        dateFilter.startDate && dateFilter.endDate
          ? `?startDate=${dateFilter.startDate}&endDate=${dateFilter.endDate}`
          : '';
      const { data } = await axios.get(`/api/dashboard/${selectedUnit.id}/stats${params}`);
      setDashboardData(data || {});
    } catch (e) {
      console.error('Erro ao buscar dados do dashboard:', e);
      setDashboardData({});
    }
  };

  const handleReturnCheck = async (id, returned) => {
    try {
      await axios.put(`/api/ausentes/${id}/retorno`, {
        retornou: returned,
        data_retorno_efetivo: returned ? new Date().toISOString().split('T')[0] : null
      });
      // Atualiza listas e dashboard para refletir taxa imediatamente
      await Promise.all([fetchPendingReturns(), fetchDashboardData()]);
    } catch (e) {
      console.error('Erro ao atualizar retorno:', e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    try {
      await axios.delete(`/api/${activeTab}/${id}`);
      fetchRecords();
    } catch (e) {
      console.error('Erro ao excluir:', e);
      alert('Não foi possível excluir. Tente novamente.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`/api/${activeTab}`, { ...formData, unidade_id: selectedUnit.id });
      setSuccess(true);
      setFormData({});
      fetchRecords();
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      console.error('Erro ao salvar:', e);
    }
    setLoading(false);
  };

  // ---------- UI handlers ----------
  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleTheme = () => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    document.body.classList.toggle('dark-mode');
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const handleLogout = () => {
    setSelectedUnit(null);
    setActiveTab('vendas');
    localStorage.removeItem('auth');
    setIsAuthed(false);
    setUnits([]);
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    const ok = loginData.email === 'bless@admin.com' && loginData.password === 'loja123';
    if (!ok) return alert('Credenciais inválidas.');
    localStorage.setItem('auth', 'true');
    setIsAuthed(true);
  };

  // ---------- Forms ----------
  const renderVendasForm = () => (
    <form onSubmit={handleSubmit} className="form-container">
      <div className="form-group">
        <label>ID do Aluno *</label>
        <input type="text" name="id_aluno" required value={formData.id_aluno || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Nome *</label>
        <input type="text" name="nome" required value={formData.nome || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Telefone *</label>
        <InputMask mask="(99) 9 9999-9999" name="telefone" required value={formData.telefone || ''} onChange={handleInputChange}>
          {(inputProps) => <input {...inputProps} type="tel" placeholder="(84) 9 9419-8689" />}
        </InputMask>
      </div>
      <div className="form-group">
        <label>Status da Venda *</label>
        <select name="status" required value={formData.status || ''} onChange={handleInputChange}>
          <option value="">Selecione...</option>
          <option value="venda_realizada">Venda Realizada</option>
          <option value="nao_vendido">Não Vendido</option>
          <option value="em_negociacao">Em Negociação</option>
          <option value="reagendado">Reagendado</option>
        </select>
      </div>
      {formData.status === 'venda_realizada' && (
        <>
          <div className="form-group">
            <label>Plano Vendido</label>
            <select name="plano_vendido" value={formData.plano_vendido || ''} onChange={handleInputChange}>
              <option value="">Selecione...</option>
              <option value="mensal">Mensal</option>
              <option value="trimestral">Trimestral</option>
              <option value="semestral">Semestral</option>
              <option value="anual">Anual</option>
              <option value="personal">Personal Trainer</option>
            </select>
          </div>
          <div className="form-group">
            <label>Valor do Plano</label>
            <input type="number" step="0.01" name="valor_plano" value={formData.valor_plano || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Forma de Pagamento</label>
            <select name="forma_pagamento" value={formData.forma_pagamento || ''} onChange={handleInputChange}>
              <option value="">Selecione...</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="pix">PIX</option>
              <option value="cartao_credito">Cartão de Crédito</option>
              <option value="cartao_debito">Cartão de Débito</option>
              <option value="boleto">Boleto</option>
            </select>
          </div>
        </>
      )}
      {formData.status === 'nao_vendido' && (
        <div className="form-group">
          <label>Motivo</label>
          <select name="motivo" value={formData.motivo || ''} onChange={handleInputChange}>
            <option value="">Selecione...</option>
            <option value="preco">Preço</option>
            <option value="localizacao">Localização</option>
            <option value="horario">Horário</option>
            <option value="estrutura">Estrutura</option>
            <option value="concorrencia">Preferiu Concorrente</option>
            <option value="desistiu">Desistiu de Treinar</option>
            <option value="outro">Outro</option>
          </select>
        </div>
      )}
      <div className="form-group">
        <label>Observações</label>
        <textarea name="observacoes" value={formData.observacoes || ''} onChange={handleInputChange} rows="3" />
      </div>
      <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'Salvando...' : 'Salvar Registro'}</button>
    </form>
  );

  const renderAusentesForm = () => (
    <form onSubmit={handleSubmit} className="form-container">
      <div className="form-group">
        <label>ID do Aluno *</label>
        <input type="text" name="id_aluno" required value={formData.id_aluno || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Nome *</label>
        <input type="text" name="nome" required value={formData.nome || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Telefone *</label>
        <InputMask mask="(99) 9 9999-9999" name="telefone" required value={formData.telefone || ''} onChange={handleInputChange}>
          {(inputProps) => <input {...inputProps} type="tel" placeholder="(84) 9 9419-8689" />}
        </InputMask>
      </div>
      <div className="form-group">
        <label>Motivo da Ausência *</label>
        <select name="motivo" required value={formData.motivo || ''} onChange={handleInputChange}>
          <option value="">Selecione...</option>
          <option value="saude">Problemas de Saúde</option>
          <option value="trabalho">Trabalho</option>
          <option value="viagem">Viagem</option>
          <option value="financeiro">Questões Financeiras</option>
          <option value="pessoal">Questões Pessoais</option>
          <option value="mudanca">Mudança de Cidade</option>
          <option value="tempo">Falta de Tempo</option>
          <option value="outro">Outro</option>
        </select>
      </div>
      <div className="form-group">
        <label>Data Prevista de Retorno</label>
        <input type="date" name="data_retorno" value={formData.data_retorno || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Observações</label>
        <textarea name="observacoes" value={formData.observacoes || ''} onChange={handleInputChange} rows="3" />
      </div>
      <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'Salvando...' : 'Salvar Registro'}</button>
    </form>
  );

  const renderCobrancasForm = () => (
    <form onSubmit={handleSubmit} className="form-container">
      <div className="form-group">
        <label>ID do Aluno *</label>
        <input type="text" name="id_aluno" required value={formData.id_aluno || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Nome *</label>
        <input type="text" name="nome" required value={formData.nome || ''} onChange={handleInputChange} />
      </div>
      <div className="form-group">
        <label>Telefone *</label>
        <InputMask mask="(99) 9 9999-9999" name="telefone" required value={formData.telefone || ''} onChange={handleInputChange}>
          {(inputProps) => <input {...inputProps} type="tel" placeholder="(84) 9 9419-8689" />}
        </InputMask>
      </div>
      <div className="form-group">
        <label>Status da Renovação *</label>
        <select name="status_renovacao" required value={formData.status_renovacao || ''} onChange={handleInputChange}>
          <option value="">Selecione...</option>
          <option value="renovado">Renovado</option>
          <option value="nao_renovado">Não Renovado</option>
          <option value="pendente">Pendente</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>
      <div className="form-group">
        <label>Motivo da Não Renovação</label>
        <select name="motivo_nao_renovacao" value={formData.motivo_nao_renovacao || ''} onChange={handleInputChange}>
          <option value="">Selecione...</option>
          <option value="financeiro">Questões Financeiras</option>
          <option value="insatisfacao">Insatisfação com Serviço</option>
          <option value="mudanca">Mudança de Cidade</option>
          <option value="concorrencia">Mudou para Concorrente</option>
          <option value="saude">Problemas de Saúde</option>
          <option value="tempo">Falta de Tempo</option>
          <option value="outro">Outro</option>
        </select>
      </div>
      <div className="form-group">
        <label>Observações</label>
        <textarea name="observacoes" value={formData.observacoes || ''} onChange={handleInputChange} rows="3" />
      </div>
      <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'Salvando...' : 'Salvar Registro'}</button>
    </form>
  );

  // ---------- Listas ----------
  const renderRetornos = () => {
    const filteredReturns = pendingReturns.filter(aluno =>
      (aluno.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (aluno.telefone || '').includes(searchTerm)
    );

    return (
      <div className="retornos-container">
        <div className="retornos-header">
          <h2>📅 Monitoramento de Retornos</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Buscar por nome ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>✕</button>
            )}
          </div>
        </div>

        <div className="retornos-stats">
          <span className="stat-item">Total: {pendingReturns.length} pendentes</span>
          {searchTerm && <span className="stat-item">Encontrados: {filteredReturns.length}</span>}
        </div>

        <div className="retornos-list">
          {filteredReturns.length === 0 ? (
            <p className="no-data">{searchTerm ? 'Nenhum retorno encontrado com essa busca' : 'Nenhum retorno pendente'}</p>
          ) : (
            filteredReturns.map((aluno) => {
              const wa = toWhatsAppLinkBR(aluno.telefone);
              return (
                <div key={aluno.id} className="retorno-card">
                  <div className="retorno-info">
                    <h3>{aluno.nome}</h3>
                    <p>📅 Retorno previsto: {new Date(aluno.data_retorno).toLocaleDateString('pt-BR')}</p>
                    <p>📱 {aluno.telefone}</p>
                  </div>
                  <div className="retorno-actions">
                    {wa && (
                      <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                        WhatsApp
                      </a>
                    )}

                    <div className="tooltip">
                      <button type="button" className="btn-info" aria-label="Mais informações">ℹ</button>
                      <div className="tooltip-content">
                        <div className="tooltip-line"><strong>Motivo:</strong> {motivoLabel(aluno.motivo || '-')}</div>
                        <div className="tooltip-line"><strong>Observações:</strong> {aluno.observacoes || '-'}</div>
                      </div>
                    </div>

                    <button className="btn-success" onClick={() => handleReturnCheck(aluno.id, true)}>
                      ✅ Retornou
                    </button>
                    <button className="btn-danger" onClick={() => handleReturnCheck(aluno.id, false)}>
                      ❌ Não Retornou
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>📊 Dashboard - {selectedUnit?.nome}</h2>
        <div className="date-filters">
          <input type="date" value={dateFilter.startDate} onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })} />
          <span>até</span>
          <input type="date" value={dateFilter.endDate} onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })} />
        </div>
      </div>

      {/* Métricas principais */}
      <div className="metrics-grid">
        <div className="metric-card">
          <h3>💰 Vendas Realizadas</h3>
          <p className="metric-value">
            {dashboardData.vendas?.find(v => v.status === 'venda_realizada')?.total || 0}
          </p>
          <p className="metric-label">
            Total: R$ {dashboardData.vendas?.find(v => v.status === 'venda_realizada')?.total_vendido || '0.00'}
          </p>
        </div>

        <div className="metric-card">
          <h3>📅 Taxa de Retorno</h3>
          <p className="metric-value">
            {dashboardData.taxaRetorno?.percentual || 0}%
          </p>
          <p className="metric-label">
            {dashboardData.taxaRetorno?.retornaram || 0} de {dashboardData.taxaRetorno?.total || 0}
          </p>
        </div>

        <div className="metric-card">
          <h3>🔄 Renovações</h3>
          <p className="metric-value">
            {dashboardData.cobrancas?.find(c => c.status_renovacao === 'renovado')?.total || 0}
          </p>
          <p className="metric-label">
            Total: {dashboardData.cobrancas?.reduce((acc, c) => acc + parseInt(c.total, 10), 0) || 0}
          </p>
        </div>

        {/* NOVA MÉTRICA: Taxa de Conversão */}
        {dashboardData.taxaConversao && (
          <div className="metric-card">
            <h3>🎯 Taxa de Conversão</h3>
            <p className="metric-value">
              {dashboardData.taxaConversao.percentual_conversao || 0}%
            </p>
            <p className="metric-label">
              {dashboardData.taxaConversao.vendas_realizadas} de {dashboardData.taxaConversao.total_contatos} contatos
            </p>
          </div>
        )}

        {/* NOVA MÉTRICA: Taxa de Cancelamento */}
        {dashboardData.taxaCancelamento && (
          <div className="metric-card">
            <h3>⚠️ Taxa de Cancelamento</h3>
            <p className="metric-value">
              {dashboardData.taxaCancelamento.percentual_cancelamento || 0}%
            </p>
            <p className="metric-label">
              {dashboardData.taxaCancelamento.cancelados} de {dashboardData.taxaCancelamento.total_cobrancas} cobranças
            </p>
          </div>
        )}
      </div>

      {/* Gráfico de vendas por mês */}
      {dashboardData.vendasPorMes && dashboardData.vendasPorMes.length > 0 && (
        <div className="chart-container" style={{ marginBottom: 30 }}>
          <h3>📈 Vendas por Mês</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[...(dashboardData.vendasPorMes || [])].reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="valor_total" stroke="#667eea" name="Valor Total (R$)" />
              <Line type="monotone" dataKey="total" stroke="#764ba2" name="Quantidade" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* NOVO: Tendência Semanal */}
      {dashboardData.tendenciaSemanal && dashboardData.tendenciaSemanal.length > 0 && (
        <div className="chart-container" style={{ marginBottom: 30 }}>
          <h3>📅 Tendência Semanal (últimas 4 semanas)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[...(dashboardData.tendenciaSemanal || [])].reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="semana" 
                tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => `Semana de ${new Date(value).toLocaleDateString('pt-BR')}`}
              />
              <Legend />
              <Bar dataKey="vendas_realizadas" fill="#667eea" name="Vendas Realizadas" />
              <Bar dataKey="total_vendas" fill="#764ba2" name="Total de Contatos" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Status de vendas */}
      {dashboardData.vendas && dashboardData.vendas.length > 0 && (
        <div className="chart-container" style={{ marginBottom: 30 }}>
          <h3>🎯 Status de Vendas</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboardData.vendas}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ status, percent }) => `${status}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="total"
              >
                {dashboardData.vendas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* NOVO: Planos Mais Vendidos */}
      {dashboardData.planosMaisVendidos && dashboardData.planosMaisVendidos.length > 0 && (
        <div className="chart-container" style={{ marginBottom: 30 }}>
          <h3>🏆 Planos Mais Vendidos</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboardData.planosMaisVendidos}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="plano_vendido" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantidade" fill="#10b981" name="Quantidade" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* NOVO: Motivos de Não Venda */}
      {dashboardData.motivosNaoVenda && dashboardData.motivosNaoVenda.length > 0 && (
        <div className="chart-container" style={{ marginBottom: 30 }}>
          <h3>❌ Motivos de Não Venda</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboardData.motivosNaoVenda}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ motivo, percent }) => `${motivo}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#ef4444"
                dataKey="total"
              >
                {dashboardData.motivosNaoVenda.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Próximos retornos */}
      {dashboardData.proximosRetornos && dashboardData.proximosRetornos.length > 0 && (
        <div className="upcoming-returns" style={{ marginBottom: 30 }}>
          <h3>🔔 Próximos Retornos Previstos</h3>
          <div className="returns-list">
            {dashboardData.proximosRetornos.map((retorno) => {
              const wa = toWhatsAppLinkBR(retorno.telefone);
              return (
                <div key={retorno.id} className="return-item">
                  <span className="return-name">{retorno.nome}</span>{' '}
                  <span className="return-date">{new Date(retorno.data_retorno).toLocaleDateString('pt-BR')}</span>{' '}
                  <span className="return-phone">{retorno.telefone}</span>
                  {wa && (
                    <> {' '}
                      <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-whatsapp sm">WhatsApp</a>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CORRIGIDO: Histórico de Retornos mostra true e false */}
      {dashboardData.retornosRecentes && dashboardData.retornosRecentes.length > 0 && (
        <div className="returns-history">
          <h3>🗂️ Histórico de Retornos (recentes)</h3>
          <div className="returns-list">
            {dashboardData.retornosRecentes.map(item => {
              const wa = toWhatsAppLinkBR(item.telefone);
              // Se tem retornou definido (true ou false), mostra o status
              let statusClass = '';
              let statusText = '';
              
              if (item.retornou === true) {
                statusClass = 'success';
                statusText = 'Retornou';
              } else if (item.retornou === false) {
                statusClass = 'danger';
                statusText = 'Não retornou';
              } else {
                statusClass = 'pending';
                statusText = 'Pendente';
              }
              
              return (
                <div key={item.id} className={`history-item ${statusClass}`}>
                  <span className="return-name">{item.nome}</span>{' '}
                  <span className="return-date">
                    Previsto: {new Date(item.data_retorno).toLocaleDateString('pt-BR')}
                    {item.data_retorno_efetivo && (
                      <> | Efetivo: {new Date(item.data_retorno_efetivo).toLocaleDateString('pt-BR')}</>
                    )}
                  </span>{' '}
                  <span className="return-phone">{item.telefone}</span>
                  <span className={`return-chip ${statusClass}`}>{statusText}</span>
                  {wa && (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-whatsapp sm"
                    >
                      WhatsApp
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- Telas ----------
  if (!isAuthed) {
    return (
      <div className={`login-screen ${darkMode ? 'dark' : ''}`}>
        <div className="login-card">
          <img src={LOGO_URL} alt="BlessFit Logo" className="logo-large" />
          <h1>Entrar</h1>
          <p className="login-subtitle">Acesse com seu usuário e senha.</p>
          <form onSubmit={handleLoginSubmit} className="login-form">
            <div className="form-group">
              <label>E-mail</label>
              <input type="email" placeholder="Seu e-mail" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required autoComplete="username" />
            </div>
            <div className="form-group">
              <label>Senha</label>
              <input type="password" placeholder="Sua senha" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required autoComplete="current-password" />
            </div>
            <button type="submit" className="submit-btn">Entrar</button>
          </form>
          <button className="theme-toggle-initial" onClick={toggleTheme}>{darkMode ? '☀️' : '🌙'}</button>
        </div>
      </div>
    );
  }

  if (!selectedUnit) {
    return (
      <div className={`unit-selection ${darkMode ? 'dark' : ''}`}>
        <div className="selection-container">
          <img src={LOGO_URL} alt="BlessFit Logo" className="logo-large" />
          <h1>Bem-vindo ao Bless Categoriza</h1>
          <p>Selecione sua unidade para continuar:</p>
          <div className="units-grid">
            {units.map((unit) => (
              <button key={unit.id} className="unit-card" onClick={() => setSelectedUnit(unit)}>
                <div className="unit-icon">🏋️</div>
                <h3>{unit.nome}</h3>
              </button>
            ))}
          </div>
          <button className="theme-toggle-initial" onClick={toggleTheme}>{darkMode ? '☀️' : '🌙'}</button>
        </div>
      </div>
    );
  }

  // ---------- App Principal ----------
  return (
    <div className={`app ${darkMode ? 'dark' : ''}`}>
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <img src={LOGO_URL} alt="BlessFit" className="logo" />
            <div>
              <h1>Bless Categoriza</h1>
              <p className="subtitle">{selectedUnit.nome}</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="theme-toggle" onClick={toggleTheme}>{darkMode ? '☀️' : '🌙'}</button>
            <button className="logout-btn" onClick={handleLogout}>🚪 Sair</button>
          </div>
        </div>
      </header>

      <div className="tab-container">
        <button className={`tab ${activeTab === 'vendas' ? 'active' : ''}`} onClick={() => setActiveTab('vendas')}>💰 Vendas</button>
        <button className={`tab ${activeTab === 'ausentes' ? 'active' : ''}`} onClick={() => setActiveTab('ausentes')}>📅 Ausentes</button>
        <button className={`tab ${activeTab === 'cobrancas' ? 'active' : ''}`} onClick={() => setActiveTab('cobrancas')}>💳 Cobranças</button>
        <button className={`tab ${activeTab === 'retornos' ? 'active' : ''}`} onClick={() => setActiveTab('retornos')}>🔄 Retornos</button>
        <button className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>📊 Dashboard</button>
      </div>

      {success && <div className="success-message">✅ Registro salvo com sucesso!</div>}

      <div className="main-content">
        {activeTab === 'dashboard' ? (
          renderDashboard()
        ) : activeTab === 'retornos' ? (
          renderRetornos()
        ) : (
          <>
            <div className="form-section">
              {activeTab === 'vendas' && renderVendasForm()}
              {activeTab === 'ausentes' && renderAusentesForm()}
              {activeTab === 'cobrancas' && renderCobrancasForm()}
            </div>

            <div className="records-section">
              <h2>Registros Recentes</h2>
              <div className="records-list">
                {records.slice(0, 5).map((record) => {
                  const wa = toWhatsAppLinkBR(record.telefone);
                  return (
                    <div key={record.id} className="record-item">
                      <div className="record-header">
                        <span className="record-name">{record.nome}</span>
                        <span className="record-date">{new Date(record.created_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <div className="record-details">
                        <span>ID: {record.id_aluno}</span>
                        <span>Tel: {record.telefone}</span>
                        {record.status && <span className="status-badge">{record.status}</span>}
                        {record.status_renovacao && <span className="status-badge">{record.status_renovacao}</span>}
                      </div>
                      <div className="record-actions" style={{ marginTop: 10 }}>
                        {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">WhatsApp</a>}
                        <button className="btn-danger" style={{ marginLeft: 10 }} onClick={() => handleDelete(record.id)}>🗑️ Excluir</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;